<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InternalChatPanelChannelList from './InternalChatPanelChannelList.vue';
import InternalChatPanelChannel from './InternalChatPanelChannel.vue';
import {
  ACTIVE_CHANNEL_STORAGE_KEY,
  closeInternalChatPanel,
} from './sidepanelUi';
import { getChannelDisplayName, getDMPeerMember, isDirectMessageChannel } from './channelDisplay';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { updateUISettings } = useUISettings();
const getChannelById = useMapGetter('internalChat/getChannelById');
const currentUserId = useMapGetter('getCurrentUserID');

const activeChannelId = ref(null);

function restoreLastChannel() {
  const stored = Number(localStorage.getItem(ACTIVE_CHANNEL_STORAGE_KEY));
  if (stored && getChannelById.value(stored)) {
    activeChannelId.value = stored;
  }
}

watch(activeChannelId, channelId => {
  if (channelId) {
    localStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, String(channelId));
  }
});

const activeChannel = computed(() =>
  activeChannelId.value ? getChannelById.value(activeChannelId.value) : null
);

const isActiveDM = computed(() => isDirectMessageChannel(activeChannel.value));

const dmPeer = computed(() =>
  getDMPeerMember(activeChannel.value, currentUserId.value)
);

const headerTitle = computed(() => {
  if (!activeChannel.value) return t('INTERNAL_CHAT.TITLE');
  return getChannelDisplayName(activeChannel.value, currentUserId.value);
});

function selectChannel(channelId) {
  activeChannelId.value = Number(channelId);
}

function goBackToList() {
  activeChannelId.value = null;
}

function closePanel() {
  closeInternalChatPanel(updateUISettings);
}

function openFullInternalChat() {
  const channel = activeChannel.value;

  if (channel) {
    router.push({
      name: isDirectMessageChannel(channel)
        ? 'internal_chat_dm'
        : 'internal_chat_channel',
      params: {
        accountId: accountId.value,
        channelId: channel.id,
      },
    });
  } else {
    router.push({
      name: 'internal_chat_home',
      params: { accountId: accountId.value },
    });
  }
  closePanel();
}

defineExpose({ restoreLastChannel });
</script>

<template>
  <div class="flex h-full w-full flex-col text-sm">
    <div
      class="flex h-12 shrink-0 items-center gap-1 border-b border-n-weak px-2"
    >
      <Button
        v-if="activeChannelId"
        v-tooltip="t('INTERNAL_CHAT.BACK')"
        icon="i-lucide-arrow-left"
        ghost
        sm
        @click="goBackToList"
      />
      <Avatar
        v-if="isActiveDM"
        :name="headerTitle"
        :src="dmPeer?.avatar_url || ''"
        :status="dmPeer?.availability_status"
        :size="24"
        rounded-full
      />
      <span class="min-w-0 flex-1 truncate px-1 text-sm font-medium text-n-slate-12">
        {{ headerTitle }}
      </span>
      <Button
        v-tooltip="t('INTERNAL_CHAT.PANEL.OPEN_FULL')"
        icon="i-lucide-external-link"
        ghost
        sm
        @click="openFullInternalChat"
      />
      <Button
        v-tooltip="$t('GENERAL.CLOSE')"
        icon="i-lucide-x"
        ghost
        sm
        @click="closePanel"
      />
    </div>
    <div class="min-h-0 flex-1">
      <InternalChatPanelChannel
        v-if="activeChannelId"
        :key="activeChannelId"
        :channel-id="activeChannelId"
      />
      <InternalChatPanelChannelList v-else @select="selectChannel" />
    </div>
  </div>
</template>
